import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddPropertyPage } from './AddPropertyPage';

import type { PropertiesTab } from '../types/properties.types';
import {
	useDeleteArea,
	useDeleteProperty,
	useDeleteSector,
	useGetAreas,
	useGetPropertyById,
	useGetSectorById,
	useGetSectors,
} from '@practium/features/api';
import { usePermissions } from '@practium/features/permissions';
import { useForBinding } from '@practium/features/forBinding';

export const ViewPropertyPageClient = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { abilities } = usePermissions();
	const { isForBinding } = useForBinding();
	const subject = isForBinding ? 'farmers-props' : 'props';
	const canEdit = abilities.can('update', subject);
	const canDeleteAbility = abilities.can('delete', subject);
	const canCreateAbility = abilities.can('create', subject);
	const type = (searchParams.get('type') as PropertiesTab) || 'properties';
	const id = searchParams.get('id') || '';

	const isProperty = type === 'properties';
	const isArea = type === 'areas';
	const isSector = type === 'sectors';

	const propertyQuery = useGetPropertyById(id, { enabled: isProperty && !!id });
	const areasQuery = useGetAreas({ enabled: isArea });
	const areaSectorsQuery = useGetSectors({ enabled: isArea });
	const sectorQuery = useGetSectorById(id, { enabled: isSector && !!id });

	const deleteProperty = useDeleteProperty();
	const deleteArea = useDeleteArea();
	const deleteSector = useDeleteSector();

	const sectorPropId = sectorQuery.data?.propId ?? '';
	const sectorAreaId = sectorQuery.data?.areaId ?? '';

	const isLoading =
		propertyQuery.isLoading ||
		(isArea && (areasQuery.isLoading || areaSectorsQuery.isLoading)) ||
		sectorQuery.isLoading;

	const handleGoToList = () => navigate('/propriedades');

	const handleCreateNext = (nextType: 'areas' | 'sectors', nextPropId: string) => {
		navigate(`/propriedades/novo?type=${nextType}&propId=${nextPropId}`);
	};

	const handleViewFlora = () => {
		navigate(`/propriedades/setores/${id}/flora?propId=${sectorPropId}`);
	};

	if (isLoading) return null;

	if (isProperty) {
		const property = propertyQuery.data;
		if (!property) return null;
		return (
			<AddPropertyPage
				type={type}
				propId={id}
				onBack={() => navigate(`/propriedades?id=${id}`)}
				initialMode="view"
				initialData={property.formData}
				onDelete={async () => {
					await deleteProperty.mutateAsync(id);
					navigate('/propriedades');
				}}
				canDelete={canDeleteAbility}
				canEdit={canEdit}
				canCreate={canCreateAbility}
				onGoToList={handleGoToList}
				onCreateNext={handleCreateNext}
				avatarUrl={property.avatarUrl}
				childCount={property.totalAreas}
			/>
		);
	}

	if (isArea) {
		const area = areasQuery.data.find((a) => a._id === id);
		if (!area) return null;
		const areaPropId = area.propId ?? '';
		const areaSectorCount = areaSectorsQuery.data.filter((s) => s.areaId === id).length;
		return (
			<AddPropertyPage
				type={type}
				propId={areaPropId}
				onBack={() => navigate(`/propriedades?id=${areaPropId}&tab=areas`)}
				initialMode="view"
				initialData={{ name: area.name, culture: area.culture, irrigation: area.irrigation }}
				onDelete={async () => {
					await deleteArea.mutateAsync({ propId: areaPropId, areaId: id });
					navigate(`/propriedades?id=${areaPropId}&tab=areas`);
				}}
				canDelete={canDeleteAbility}
				canEdit={canEdit}
				canCreate={canCreateAbility}
				onGoToList={handleGoToList}
				onCreateNext={handleCreateNext}
				avatarUrl={area.avatar?.url ?? undefined}
				childCount={areaSectorCount}
			/>
		);
	}

	const sector = sectorQuery.data;
	if (!sector) return null;

	return (
		<AddPropertyPage
			type={type}
			propId={sectorPropId}
			onBack={() => navigate(`/propriedades?id=${sectorPropId}&tab=sectors`)}
			initialMode="view"
			initialData={sector.formData}
			onDelete={async () => {
				await deleteSector.mutateAsync({ propId: sectorPropId, areaId: sectorAreaId, sectorId: id });
				navigate(`/propriedades?id=${sectorPropId}&tab=sectors`);
			}}
			canDelete={canDeleteAbility}
			canEdit={canEdit}
			avatarUrl={sector.avatarUrl}
			onIaAnalysis={handleViewFlora}
		/>
	);
};
